/* ============================================================
   Melusio header behaviour (Cut 2):
   - announcement dismiss (sessionStorage); --header-offset tracks the measured
     header height (ResizeObserver, no scroll or resize listener)
   - condense past 40px via a sentinel + IntersectionObserver (no scroll listener)
   - bag badge reconcile from {{ routes.cart_url }}.js (cache-safe; Cut 3 hook)
   - predictive search (debounced, ARIA listbox, keyboard nav, states) + chips
   ============================================================ */
(function () {
  const root = document.documentElement;
  const setOffset = (px) => root.style.setProperty('--header-offset', px + 'px');

  let started = false;
  function init() {
    if (started) return; started = true;

    const header = document.querySelector('.m-header');

    /* ---- header offset = the header's measured height ----
       This used to be two constants, 98 with the announcement bar and 60
       without. Neither was ever the real height below about 480px: the
       announcement text wraps to a second line there and the bar grows to
       120px, so 22px of every non-home template sat under the fixed header.

       CSS cannot fix it. It cannot read a rendered height, and it cannot even
       override the value — this line writes --header-offset as an inline style
       on <html>, which no :root rule can beat. Making the header sticky so it
       reserves its own height was the one CSS-only route and it does not work
       either: the header's containing block is exactly its own height, so it
       scrolls away instead of sticking. Measured on the real page.

       ResizeObserver is the smallest thing that tracks the height: one element,
       no scroll listener and no resize listener. It also covers the dismiss
       below for free, since hiding the bar changes the height it is watching.
       Condensing only repaints the hairline, so the height holds while
       scrolling and content does not shift under the reader. */
    const syncOffset = () => { if (header) setOffset(Math.round(header.getBoundingClientRect().height)); };
    if (header && 'ResizeObserver' in window) new ResizeObserver(syncOffset).observe(header);

    /* ---- announcement bar ----
       Dismissal is SESSION-scoped: the bar is a promotional line a merchant
       edits, so a dismissal that outlived the session would bury the next drop
       behind the last one. sessionStorage is the theme's existing pattern for
       this (melusio-notify.js does the same); localStorage is reserved for the
       theme preference, which is a stated choice rather than a dismissal.

       Both accesses are wrapped, which they were not. Storage throws outright
       when a browser is set to block site data, and an uncaught throw here does
       not just lose the dismissal — it aborts init() and takes the condense
       observer, the bag badge, the theme toggle and predictive search with it.
       Denied storage falls back to showing the bar, which is the right way for
       an announcement to fail.

       The attribute on <html> is what actually hides the bar (see the rule in
       melusio-sections.css); melusio-styles.liquid sets the same attribute
       before the first paint on later page views. Writing it here as well keeps
       one mechanism rather than two, and `hidden` stays alongside it so the bar
       leaves the accessibility tree by its own semantics. */
    const bar = document.querySelector('[data-announce]');
    const store = (k, v) => { try { sessionStorage.setItem(k, v); } catch (e) {} };
    const read = (k) => { try { return sessionStorage.getItem(k); } catch (e) { return null; } };
    if (bar) {
      if (read('melusio-announce') === 'dismissed') {
        bar.hidden = true;
        root.setAttribute('data-announce-dismissed', '');
      } else {
        const close = bar.querySelector('[data-announce-close]');
        close && close.addEventListener('click', () => {
          bar.hidden = true;
          root.setAttribute('data-announce-dismissed', '');
          store('melusio-announce', 'dismissed');
          syncOffset();
        });
      }
    }
    syncOffset();

    /* ---- condense on scroll past 40px (sentinel + IO) ---- */
    if (header && 'IntersectionObserver' in window) {
      const sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'position:absolute;top:40px;left:0;width:1px;height:1px;pointer-events:none;';
      document.body.prepend(sentinel);
      new IntersectionObserver(([e]) => {
        header.classList.toggle('is-condensed', !e.isIntersecting);
      }, { threshold: 0 }).observe(sentinel);
    }

    /* ---- bag badge reconcile from /cart.js (server count can be cache-stale) ---- */
    const cartUrl = (header && header.dataset.cartUrl) || '/cart';
    function setCount(count) {
      document.querySelectorAll('[data-bag-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = !(count > 0);
      });
      document.querySelectorAll('[data-bag]').forEach((el) => el.setAttribute('data-count', count));
    }
    async function reconcile() {
      try {
        const res = await fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const cart = await res.json();
        setCount(cart.item_count);
      } catch (_) {}
    }
    // merge (not replace) so melusio-cart.js's methods survive regardless of load order
    window.MelusioCart = Object.assign(window.MelusioCart || {}, { reconcile, setCount });
    reconcile();

    /* ---- theme toggle ----
       Dark is the default (no attribute, nothing stored); only 'light' is
       persisted. The attribute itself is set pre-paint by the blocking boot
       script in melusio-styles.liquid — here we only flip it + keep the
       button's ARIA state honest (the server always renders the dark state). */
    // Two toggles: the bar's icon (desktop) and the menu's row (phones, #211). Both
    // flip the same attribute, and both are kept honest after either is pressed. The
    // menu row shows its label as text; the bar's icon carries it as its name.
    const themeToggles = Array.from(document.querySelectorAll('[data-theme-toggle]'));
    if (themeToggles.length) {
      const syncThemeToggles = () => {
        const light = root.getAttribute('data-theme') === 'light';
        themeToggles.forEach((toggle) => {
          toggle.setAttribute('aria-pressed', String(light));
          // Labels come from the header section settings via data attributes.
          const label = toggle.getAttribute(light ? 'data-label-dark' : 'data-label-light');
          if (!label) return;
          const text = toggle.querySelector('[data-theme-toggle-text]');
          if (text) text.textContent = label;
          else toggle.setAttribute('aria-label', label);
        });
      };
      themeToggles.forEach((toggle) => toggle.addEventListener('click', () => {
        const toLight = root.getAttribute('data-theme') !== 'light';
        if (toLight) root.setAttribute('data-theme', 'light');
        else root.removeAttribute('data-theme');
        try {
          if (toLight) localStorage.setItem('melusio-theme', 'light');
          else localStorage.removeItem('melusio-theme');
        } catch (_) {}
        syncThemeToggles();
      }));
      syncThemeToggles();
    }

    /* ---- predictive search ---- */
    const form = document.querySelector('[data-search-form]');
    if (form) initSearch(form);
  }

  function initSearch(form) {
    const input = form.querySelector('input[name="q"]');
    const listbox = form.querySelector('[data-search-results]');
    const statusEl = form.querySelector('[data-search-status]');
    const region = form.closest('[data-search-region]') || form;
    const predictiveUrl = form.dataset.predictiveUrl;
    const sectionId = form.dataset.predictiveSection || 'melusio-predictive-search';
    const MIN = 2;
    let controller = null;
    let activeIndex = -1;

    // Status copy comes from the "Text: search" theme settings via data
    // attributes on the form; [count]/[terms] are literal tokens substituted here.
    const STATUS = {
      searching: form.dataset.statusSearching,
      one: form.dataset.statusOne,
      other: form.dataset.statusOther,
      none: form.dataset.statusNone,
    };
    const statusText = (tpl, count, term) => (tpl ? tpl.replace('[count]', count).replace('[terms]', term) : '');

    const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
    const setState = (s) => { region.setAttribute('data-state', s); input.setAttribute('aria-expanded', s === 'results' || s === 'noresults' ? 'true' : 'false'); };
    const options = () => Array.from(listbox ? listbox.querySelectorAll('[role="option"]') : []);

    function say(msg) { if (statusEl) statusEl.textContent = msg; }

    async function run(term) {
      term = (term || '').trim();
      activeIndex = -1;
      if (!predictiveUrl || term.length < MIN) { if (listbox) listbox.innerHTML = ''; setState('idle'); say(''); return; }
      setState('loading'); say(STATUS.searching || '');
      if (controller) controller.abort();
      controller = new AbortController();
      const url = new URL(predictiveUrl, location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', sectionId);
      try {
        const res = await fetch(url, { signal: controller.signal });
        const html = await res.text();
        if (listbox) listbox.innerHTML = html;
        const opts = options();
        opts.forEach((o, i) => { o.id = o.id || `m-ps-opt-${i}`; o.setAttribute('aria-selected', 'false'); });
        if (opts.length) { setState('results'); say(statusText(opts.length === 1 ? STATUS.one : STATUS.other, opts.length, term)); }
        else { setState('noresults'); say(statusText(STATUS.none, 0, term)); }
      } catch (e) { if (e.name !== 'AbortError') { setState('idle'); say(''); } }
    }

    function move(delta) {
      const opts = options();
      if (!opts.length) return;
      if (activeIndex >= 0 && opts[activeIndex]) opts[activeIndex].setAttribute('aria-selected', 'false');
      activeIndex = (activeIndex + delta + opts.length) % opts.length;
      const el = opts[activeIndex];
      el.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', el.id);
      el.scrollIntoView({ block: 'nearest' });
    }

    input.addEventListener('input', debounce((e) => run(e.target.value), 250));
    input.addEventListener('keydown', (e) => {
      const opts = options();
      if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
      else if (e.key === 'Enter') {
        if (activeIndex >= 0 && opts[activeIndex]) {
          const link = opts[activeIndex].querySelector('a[href]') || opts[activeIndex].closest('a[href]');
          if (link) { e.preventDefault(); window.location.href = link.href; }
        }
        // else: let the form submit → /search?q=…
      }
    });

    // chips fill + search
    form.querySelectorAll('[data-search-chip]').forEach((chip) => {
      chip.addEventListener('click', () => {
        input.value = chip.getAttribute('data-search-chip') || chip.textContent.trim();
        input.focus();
        run(input.value);
      });
    });

    setState('idle');
  }

  document.addEventListener('DOMContentLoaded', init);
  if (document.readyState !== 'loading') init();
})();
